import { LoaderFunction } from 'react-router-dom';
import { AuthContextType } from '../../../../hooks/useAuth';

// GQL
import { client } from '../../../../gql/client';
import { selectAllOrders } from '../../../../gql/selectors/orders';
import { GET_GENERIC_ORDERS } from '../../../../gql/queries/genericOrders';
import {
  FETCH_CAPITOLS,
  FETCH_SUB_CAPITOLS,
  FETCH_CLASSIFICATIONS,
  FETCH_PREDEFINED_ORDERS,
} from '../../../../gql/queries/orders/predefinedData';
import { GET_CONCESSIONS } from '../../../../gql/queries/orders/orders';
import { FETCH_TEAMS, FETCH_USERS } from '../../../../gql/queries/users';

// Redux
import { getCursor } from '../../../../state/selectors/ui/tables';
import { store } from '../../../../state/configureStore';

// Types
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';
import { loadingData } from '../../../../state/actions/ui/loadingData';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
export const LoaderOrderDuplicates: (
  userData: AuthContextType | null
) => LoaderFunction = (userData) => async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  const cursorGenericOrder = getCursor(tableIds.DUPLICATE_GENERIC_ORDER)(
    store.getState()
  );
  const dispatch = store.dispatch;

  const roleActive = userData?.user?.user?.rolActive;

  if (userData?.user) {
    // FIX A4: Si concessionId no está seteada, pre-setear si solo hay 1 concesión.
    let effectiveConcessionId = cursorGenericOrder.concessionId;
    if (!effectiveConcessionId) {
      try {
        const { data: { concessions } } = await client.query({ query: GET_CONCESSIONS });
        if (concessions?.length === 1) {
          effectiveConcessionId = concessions[0].id;
          dispatch(setCursor(tableIds.DUPLICATE_GENERIC_ORDER, { ...cursorGenericOrder, concessionId: effectiveConcessionId }));
        }
      } catch { /* continue without concessionId */ }
    }

    // FIX (loader-loading-flag-leak): cierra cualquier flag fugado por un run
    // anterior interrumpido.
    dispatch(loadingData(tableIds.DUPLICATE_GENERIC_ORDER, false));

    const readWorkOrders = client.readQuery({
      query: GET_GENERIC_ORDERS,
      variables: {
        input: {
          take: cursorGenericOrder.take,
          cursor: cursorGenericOrder.cursor,
          sortField: cursorGenericOrder.sortField,
          orderBy: cursorGenericOrder.orderBy ?? 'desc',
          concessionId: effectiveConcessionId,
          teamIds: cursorGenericOrder.teamIds ?? [],
          fromDate: cursorGenericOrder.fromDate,
          toDate: cursorGenericOrder.toDate,
          indexType: cursorGenericOrder.indexType,
          indexExpedition: cursorGenericOrder.indexExpedition,
          capitolId: cursorGenericOrder.capitolId,
          subCapitolId: cursorGenericOrder.subCapitolId,
          classification: cursorGenericOrder?.classification,
          predefinedOrderId: cursorGenericOrder?.predefinedOrderId,
          states: cursorGenericOrder?.states,
          userIds: cursorGenericOrder?.userIds,
          pkInitKm: cursorGenericOrder?.pkInitKm,
          pkInitMeter: cursorGenericOrder?.pkInitMeter,
          pkEndKm: cursorGenericOrder?.pkEndKm,
          pkEndMeter: cursorGenericOrder?.pkEndMeter,
          description: cursorGenericOrder?.description,
          track: cursorGenericOrder?.track,
        },
      },
    });
    const pageInfo = readWorkOrders?.genericOrders?.pageInfo;

    const totalCount = readWorkOrders?.genericOrders?.totalCount;
    const isCacheEmpty = JSON.stringify(readWorkOrders) === '{}';

    const genericOrders =
      readWorkOrders &&
      !isCacheEmpty &&
      (await selectAllOrders(readWorkOrders?.genericOrders?.genericOrders));

    const [
      {
        data: { capitols },
      },
      {
        data: { concessions },
      },
      {
        data: { subCapitols },
      },
      {
        data: { classifications },
      },
      {
        data: { teams },
      },
      {
        data: { predefinedOrders },
      },
      {
        data: { users },
      },
    ] = await Promise.all([
      client.query({
        query: FETCH_CAPITOLS,
      }),
      client.query({
        query: GET_CONCESSIONS,
      }),
      client.query({
        query: FETCH_SUB_CAPITOLS,
      }),
      client.query({
        query: FETCH_CLASSIFICATIONS,
      }),
      client.query({
        query: FETCH_TEAMS,
      }),
      client.query({
        query: FETCH_PREDEFINED_ORDERS,
        fetchPolicy: 'cache-first',
      }),
      client.query({
        query: FETCH_USERS,
        fetchPolicy: 'cache-first',
      }),
    ]);

    const filtersDataOptions = {
      capitols,
      concessions,
      subCapitols,
      classifications,
      teams,
      predefinedOrders,
      users,
    };

    const combinedData = {
      genericOrders,
      filtersDataOptions,
      totalCount,
      pageInfo,
      roleActive,
    };

    if (!readWorkOrders || isCacheEmpty) {
      dispatch(loadingData(tableIds.DUPLICATE_GENERIC_ORDER, true));
      try {
      let data;
      if (isCacheEmpty) {
        // Tarea #11: respetar el take guardado en cursor (page-size selector).
        // Espejo de DuplicateComunication/loader.
        const effectiveTake = firstPageTake(cursorGenericOrder?.take, takeBase);
        data = await client.query({
          query: GET_GENERIC_ORDERS,
          variables: {
            input: {
              take: effectiveTake,
              cursor: '',
              sortField: cursorGenericOrder.sortField,
              orderBy: cursorGenericOrder.orderBy ?? 'desc',
              concessionId: effectiveConcessionId,
              teamIds: cursorGenericOrder.teamIds ?? [],
              fromDate: cursorGenericOrder.fromDate,
              toDate: cursorGenericOrder.toDate,
              indexType: cursorGenericOrder.indexType,
              indexExpedition: cursorGenericOrder.indexExpedition,
              capitolId: cursorGenericOrder.capitolId,
              subCapitolId: cursorGenericOrder.subCapitolId,
              classification: cursorGenericOrder?.classification,
              predefinedOrderId: cursorGenericOrder?.predefinedOrderId,
              states: cursorGenericOrder?.states,
              userIds: cursorGenericOrder?.userIds,
              pkInitKm: cursorGenericOrder?.pkInitKm,
              pkInitMeter: cursorGenericOrder?.pkInitMeter,
              pkEndKm: cursorGenericOrder?.pkEndKm,
              pkEndMeter: cursorGenericOrder?.pkEndMeter,
              description: cursorGenericOrder?.description,
              track: cursorGenericOrder?.track,
            },
          },
          fetchPolicy: 'cache-first',
        });

        const setCursorData = {
          take: effectiveTake,
          cursor: '',
          sortField: cursorGenericOrder.sortField,
          orderBy: cursorGenericOrder.orderBy ?? 'desc',
          concessionId: effectiveConcessionId,
          teamIds: cursorGenericOrder.teamIds ?? [],
          fromDate: cursorGenericOrder.fromDate,
          toDate: cursorGenericOrder.toDate,
          indexType: cursorGenericOrder.indexType,
          indexExpedition: cursorGenericOrder.indexExpedition,
          capitolId: cursorGenericOrder.capitolId,
          subCapitolId: cursorGenericOrder.subCapitolId,
          classification: cursorGenericOrder?.classification,
          predefinedOrderId: cursorGenericOrder?.predefinedOrderId,
          states: cursorGenericOrder?.states,
          userIds: cursorGenericOrder?.userIds,
          pkInitKm: cursorGenericOrder?.pkInitKm,
          pkInitMeter: cursorGenericOrder?.pkInitMeter,
          pkEndKm: cursorGenericOrder?.pkEndKm,
          pkEndMeter: cursorGenericOrder?.pkEndMeter,
          description: cursorGenericOrder?.description,
          track: cursorGenericOrder?.track,
        };

        dispatch(setCursor(tableIds.DUPLICATE_GENERIC_ORDER, setCursorData));
      } else {
        data = await client.query({
          query: GET_GENERIC_ORDERS,
          variables: {
            input: {
              take: cursorGenericOrder.take,
              cursor: cursorGenericOrder.cursor,
              sortField: cursorGenericOrder.sortField,
              orderBy: cursorGenericOrder.orderBy ?? 'desc',
              concessionId: effectiveConcessionId,
              teamIds: cursorGenericOrder.teamIds ?? [],
              fromDate: cursorGenericOrder.fromDate,
              toDate: cursorGenericOrder.toDate,
              indexType: cursorGenericOrder.indexType,
              indexExpedition: cursorGenericOrder.indexExpedition,
              capitolId: cursorGenericOrder.capitolId,
              subCapitolId: cursorGenericOrder.subCapitolId,
              classification: cursorGenericOrder?.classification,
              predefinedOrderId: cursorGenericOrder?.predefinedOrderId,
              states: cursorGenericOrder?.states,
              userIds: cursorGenericOrder?.userIds,
              pkInitKm: cursorGenericOrder?.pkInitKm,
              pkInitMeter: cursorGenericOrder?.pkInitMeter,
              pkEndKm: cursorGenericOrder?.pkEndKm,
              pkEndMeter: cursorGenericOrder?.pkEndMeter,
              description: cursorGenericOrder?.description,
              track: cursorGenericOrder?.track,
            },
          },
          fetchPolicy: 'cache-first',
        });
      }

      const loading = data.loading;
      const pageInfo = data.data.genericOrders.pageInfo;

      const totalCount = data.data.genericOrders.totalCount;
      const genericOrders = await selectAllOrders(
        data.data.genericOrders.genericOrders
      );

      const [
        {
          data: { capitols },
        },
        {
          data: { concessions },
        },
        {
          data: { subCapitols },
        },
        {
          data: { classifications },
        },
        {
          data: { teams },
        },
        {
          data: { predefinedOrders },
        },
        {
          data: { users },
        },
      ] = await Promise.all([
        client.query({
          query: FETCH_CAPITOLS,
        }),
        client.query({
          query: GET_CONCESSIONS,
        }),
        client.query({
          query: FETCH_SUB_CAPITOLS,
        }),
        client.query({
          query: FETCH_CLASSIFICATIONS,
        }),
        client.query({
          query: FETCH_TEAMS,
        }),
        client.query({
          query: FETCH_PREDEFINED_ORDERS,
          fetchPolicy: 'cache-first',
        }),
        client.query({
          query: FETCH_USERS,
          fetchPolicy: 'cache-first',
        }),
      ]);

      const filtersDataOptions = {
        capitols,
        concessions,
        subCapitols,
        classifications,
        teams,
        predefinedOrders,
        users,
      };

      const combinedData = {
        genericOrders,
        filtersDataOptions,
        totalCount,
        pageInfo,
        loading,
        roleActive,
      };
      return combinedData;
      } finally {
        // FIX (loader-loading-flag-leak): cierra el flag aunque MISS lance.
        dispatch(loadingData(tableIds.DUPLICATE_GENERIC_ORDER, false));
      }
    }

    return combinedData;
  }
  return {};
};

  return { data: load() };
};
